import { useNavigate } from "@tanstack/react-router";
import { Announcement03, ShoppingBag01 } from "@untitledui/icons";
import { useMemo } from "react";
import { Focusable } from "react-aria-components";
import type { OpenCollaborationListItem } from "@/api/collaborations";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn, UnboxTableProps } from "@/components/application/table/unbox-table.types";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { useMerchant } from "@/providers/MerchantProvider";
import { GmvCell } from "./gmv-cell";
import { OverdueCell } from "./overdue-cell";

const numberFormatter = new Intl.NumberFormat("en-US");

const formatCurrency = (value?: number, currency?: string) => {
  if (value === undefined) return "-";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency || "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value?: number) => {
  if (value === undefined || value === null) return "-";
  return `${(value * 100).toFixed(2)}%`;
};

export interface OpenCollaborationsTableProps
  extends Omit<
    UnboxTableProps<OpenCollaborationListItem>,
    "columns" | "rowId" | "emptyState" | "filteredEmptyState" | "size" | "rounded" | "className" | "aria-label"
  > {
  onClearFilters: () => void;
}

export const OpenCollaborationsTable = ({
  rows,
  sorting,
  pagination,
  hasActiveFilters,
  onClearFilters,
}: OpenCollaborationsTableProps) => {
  const navigate = useNavigate();
  const merchant = useMerchant();

  const handleRowClick = (collaborationId: string) => {
    navigate({
      to: "/merchant/$merchantId/collaborations/open/$collaborationId",
      params: { merchantId: merchant, collaborationId },
    });
  };

  const columns = useMemo<UnboxTableColumn<OpenCollaborationListItem>[]>(
    () => [
      {
        id: "productTitle",
        label: "Name",
        sortable: true,
        minWidth: "240px",
        render: (_value, row) => (
          <div className="flex flex-row items-center gap-2 max-w-[360px]">
            {row.productImageUrl ? (
              <img
                src={row.productImageUrl}
                alt={row.productTitle}
                className="h-10 w-10 rounded object-cover flex-shrink-0"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100 flex-shrink-0">
                <ShoppingBag01 className="h-5 w-5 text-gray-400" />
              </div>
            )}
            <div className="flex flex-col min-w-0">
              <span className="inline-flex w-full min-w-0 justify-start">
                <Tooltip title={row.productTitle}>
                  <Focusable>
                    <span className="truncate text-sm font-medium text-primary">{row.productTitle}</span>
                  </Focusable>
                </Tooltip>
              </span>
              {row.productPrice !== undefined && (
                <span className="text-xs text-tertiary">
                  {formatCurrency(row.productPrice, row.productPriceCurrency)}
                </span>
              )}
            </div>
          </div>
        ),
      },
      {
        id: "totalGmv",
        label: "Total GMV",
        minWidth: "120px",
        render: (_value, row) => <GmvCell value={row.totalGmv} currency={row.totalGmvCurrency} />,
      },
      {
        id: "stock",
        label: "Stock",
        sortable: true,
        minWidth: "80px",
        render: (_value, row) => (row.stock !== undefined ? numberFormatter.format(row.stock) : "0"),
      },
      {
        id: "standardRate",
        label: "Standard rate",
        sortable: true,
        minWidth: "120px",
        render: (_value, row) => formatPercent(row.standardRate),
      },
      {
        id: "sampleRequestedCount",
        label: "Sample requests",
        minWidth: "130px",
        render: (_value, row) => (row.sampleRequestedCount ? numberFormatter.format(row.sampleRequestedCount) : "-"),
      },
      {
        id: "postOverdueCount",
        label: "Post Overdue",
        minWidth: "130px",
        render: (_value, row) => <OverdueCell count={row.postOverdueCount} />,
      },
      {
        id: "videoCount",
        label: "Videos",
        minWidth: "100px",
        render: (_value, row) => (row.videoCount > 0 ? numberFormatter.format(row.videoCount) : "0"),
      },
    ],
    [],
  );

  return (
    <UnboxTable<OpenCollaborationListItem>
      aria-label="Open Collaborations"
      className="max-h-[60vh]"
      columns={columns}
      rows={rows}
      rowId="collaborationId"
      onClickRow={(row) => handleRowClick(row.collaborationId)}
      sorting={sorting}
      pagination={pagination}
      hasActiveFilters={hasActiveFilters}
      emptyState={{
        icon: Announcement03,
        title: "Create a collaboration to start selling with target creators",
        description: "Create a target collaboration to invite specific creators to sell your products.",
      }}
      filteredEmptyState={{ onClearFilters }}
    />
  );
};
